/* eslint-disable */
import cn from 'classnames';
import Link from 'next/link';
import { Scrollbar } from 'react-scrollbars-custom';

import { useRouter } from 'next/navigation';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchMutation } from '@shared/api';
import { getNounForm } from '@shared/libs';
import type { Locale } from '@shared/libs';

import styles from './search-form.module.css';

type TProps = {
  placeholderText?: string | undefined;
  isMobile?: boolean;
  callback?: () => void;
  q?: string;
  locale: Locale;
};

const FLAG = true;

const debounce = (func, wait = 200) => {
  let timeout; // for the setTimeout function and so it can be cleared
  function executedFunction(...args) {
    // the function returned from debounce
    const later = () => {
      // this is the delayed function
      clearTimeout(timeout); // clears the timeout when the function is called
      func(...args); // calls the function
    };
    clearTimeout(timeout); // this clears the timeout each time the function is run again preventing later from running until we stop calling the function
    timeout = setTimeout(later, wait); // this sets the time out to run after the wait period
  }
  executedFunction.cancel = function () {
    // so can be cancelled
    clearTimeout(timeout); // clears the timeout
  };
  return executedFunction;
};

function useDebounce(callback, delay = 1000, deps = []) {
  // debounce the callback
  const debouncedCallback = React.useCallback(debounce(callback, delay), [delay, ...deps]); // with the delay
  // clean up on unmount or dependency change
  React.useEffect(() => {
    return () => {
      debouncedCallback.cancel(); // cancel any pending calls
    };
  }, [delay, ...deps]);
  // return the debounce function so we can use it
  return debouncedCallback;
}

type TResult = {
  id: string | number;
  title: string;
  subTitle: string;
  url: string;
  // type: 'musician' | 'popular' | 'note';
}[];

export const SearchForm = ({
  placeholderText = '',
  isMobile = false,
  callback,
  q = '',
  locale,
}: TProps) => {
  const { mutate, data, isLoading, isSuccess } = useSearchMutation();
  const router = useRouter();
  const ref = useRef<Scrollbar>();
  const [search, setSearch] = useState<string>(q);
  const [isFocus, setIsFocus] = useState(true);
  const [keyActive, setKeyActive] = useState<number | null>(null);
  const [result, setResult] = useState<TResult | null>(null);

  const handleClickLink = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      if (!search || search === q) {
        evt.preventDefault();
      }
    },
    [search, q],
  );

  const fetchData = useDebounce((value: string) => {
    if (value.length > 2) {
      mutate({ q: value });
    }
  }, 300);

  useEffect(() => {
    if (q !== search) {
      FLAG && fetchData(search);
    }
  }, [search, q]);

  useEffect(() => {
    if (isFocus && data?.data) {
      const resultData: TResult = [];
      const { popular, musicians, notes } = data.data;
      notes.forEach(({ title, url, id, musicians: noteMusicians, arrangement, instruments }) => {
        resultData.push({
          title: `${title} - ${noteMusicians?.map(({ title }) => title).join(', ')}`,
          url,
          id: `${id}-notes`,
          subTitle: `${locale.SEARCH_HINT_NOTES || 'SEARCH_HINT_NOTES'} - ${arrangement?.title} | ${instruments?.[0]?.title}`,
        });
      });
      musicians.forEach(({ title, url, id }) => {
        resultData.push({
          title,
          url,
          id: `${id}-musicians`,
          subTitle: locale.SEARCH_HINT_MUSICIANS || 'SEARCH_HINT_MUSICIANS',
        });
      });
      popular.forEach(({ title, url, id }) => {
        resultData.push({
          title,
          url,
          id: `${id}-popular`,
          subTitle: locale.SEARCH_HINT_RUBRIC || 'SEARCH_HINT_RUBRIC',
        });
      });
      setResult(resultData);
    }
  }, [data?.data]);

  useEffect(() => {
    setKeyActive(null);
  }, [result]);

  useEffect(() => {
    const listener = (event) => {
      if (result && (event.code === 'ArrowDown' || event.code === 'ArrowUp')) {
        event.preventDefault();
        setKeyActive((prev) => {
          if (prev === null) {
            return 0;
          }
          let newKey = 0;

          if (event.code === 'ArrowDown') {
            newKey = prev > result.length - 2 ? 0 : prev + 1;
          }
          if (event.code === 'ArrowUp') {
            newKey = prev === 0 ? result.length - 1 : prev - 1;
          }

          if (ref.current?.contentElement?.parentElement) {
            const { contentElement } = ref.current;
            const parentElement = contentElement.parentElement;
            if (parentElement) {
              const scrollTop = parentElement.scrollTop;
              const height = parentElement.offsetHeight;
              const targetItem = contentElement.children.item(newKey);

              const y0 = scrollTop;
              const y2 = height + scrollTop;

              if (targetItem) {
                const topElement = (targetItem as unknown as { offsetTop: number }).offsetTop;
                const heightElement = targetItem.clientHeight;
                const bottomElement = topElement + heightElement;

                if (topElement < y0) {
                  parentElement.scrollTo({
                    behavior: 'smooth',
                    top: topElement,
                  });
                }
                if (bottomElement > y2) {
                  parentElement.scrollTo({
                    behavior: 'smooth',
                    top: bottomElement - height,
                  });
                }
              }
            }
          }
          return newKey;
        });
      }
      if (result && event.code === 'Enter') {
        setKeyActive((prev) => {
          if (prev !== null) {
            event.preventDefault();
            const item = result[prev];

            if (item) {
              setIsFocus(false);
              setResult(null);
              setKeyActive(null);
              callback?.();
              router.push(item.url);
            }
          }

          return null;
        });
      }
    };

    document.addEventListener('keydown', listener);

    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [result, callback]);

  return (
    <div
      className={cn(styles.searchInputWrapper, {
        [styles.isMobile]: isMobile,
      })}
    >
      <input
        className={styles.searchInput}
        placeholder={placeholderText}
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setSearch(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && search) {
            router.push(`/search/?q=${search.toLocaleLowerCase()}`);
            callback?.();
          }
        }}
        onFocus={() => {
          setIsFocus(true);
          setResult(null);
          setKeyActive(null);
        }}
        onBlur={() => {
          setTimeout(() => {
            setIsFocus(false);
            setResult(null);
            setKeyActive(null);
          }, 300);
        }}
      />
      <Link
        className={styles.searchButton}
        href={`/search/?q=${search.toLocaleLowerCase()}`}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          handleClickLink(e);
          callback?.();
        }}
        prefetch={false}
      >
        <span className={styles.searchButtonImg} title="search" />
      </Link>
      <div className={styles.loaderWrap}>
        {isLoading && <div className={styles.loader}></div>}
        {!isLoading && search.length ? (
          <div
            className={styles.close}
            onClick={() => {
              setSearch('');
            }}
          />
        ) : null}
      </div>
      {FLAG && isFocus && result && result.length > 0 && !isLoading && search.length > 2 && (
        <div className={styles.result}>
          <Scrollbar
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            ref={ref}
            style={{ maxHeight: 400, height: 53 * result.length }}
          >
            {result.map(({ title, url, id, subTitle }, index) => (
              <Link
                key={id}
                href={url}
                className={cn(styles.link, {
                  [styles.active]: index === keyActive,
                })}
                onClick={(event) => {
                  callback?.();
                  event.stopPropagation();
                }}
              >
                <span className={styles.title}>{title}</span>
                <span className={styles.subTitle}>{subTitle}</span>
              </Link>
            ))}
            {data?.pager?.records_all && data?.pager?.records_all > 5 && (
              <Link
                href={`/search/?q=${search.toLocaleLowerCase()}`}
                className={cn(styles.link, {
                  [styles.active]: result.length === keyActive,
                })}
                onClick={(event) => {
                  callback?.();
                  event.stopPropagation();
                }}
              >
                <span className={styles.title}>
                  {locale.PAGE_SERCH_RESULT_SHOW_MORE || 'PAGE_SERCH_RESULT_SHOW_MORE'}
                </span>
                <span className={styles.subTitle}>
                  {data.pager.records_all - 5}{' '}
                  {getNounForm(data.pager.records_all - 5, [
                    locale.NOTES_ONE || 'NOTES_ONE',
                    locale.NOTES_TWO || 'NOTES_TWO',
                    locale.NOTES_MANY || 'NOTES_MANY',
                  ])}
                </span>
              </Link>
            )}
          </Scrollbar>
        </div>
      )}
      {FLAG &&
        isFocus &&
        result &&
        result.length === 0 &&
        !isLoading &&
        isSuccess &&
        search.length > 2 && (
          <div className={styles.result}>
            <Link
              href={`/search/?q=${search.toLocaleLowerCase()}`}
              className={styles.resultEmpty}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                handleClickLink(e);
                callback?.();
              }}
              style={{ display: 'block' }}
              prefetch={false}
            >
              <span className={styles.emptyTitle}>
                {locale.PAGE_SERCH_NO_RESULT_BTN || 'PAGE_SERCH_NO_RESULT_BTN'}
              </span>
              <span className={styles.empty}>
                {locale.PAGE_SERCH_NO_RESULT_1} "{search}" {locale.PAGE_SERCH_NO_RESULT_2}
              </span>
            </Link>
          </div>
        )}
    </div>
  );
};
